// src/lib/analytics-navigation-guard.ts
//
// Defense-in-depth for the navigation race between the browser's history
// APIs and the React route effect that operates the ga-disable kill switch.
//
// Threat model: gtag.js Enhanced Measurement can observe history transitions
// on its own ("page changes based on browser history events") and emit a
// page_view carrying the NEW location — a private route path, an opaque
// identifier, or a raw query string — in the window between the history
// change and the providers effect running handleRouteChange. That window is
// reachable in production: the Header links every public page to /patient,
// and the blog filter UI rewrites the URL with health-topic query tokens
// (?category=…&tag=…) via history.pushState.
//
// Model: synchronous quarantine, acknowledged release.
// - Before an unsafe history transition (target pathname untrackable, or a
//   target URL that introduces a query string), the guard sets the official
//   ga-disable flag SYNCHRONOUSLY, so gtag.js drops every hit for this
//   measurement ID — including Enhanced Measurement — for the remainder of
//   the transition, regardless of its own listeners or wrappers.
// - The guard never re-enables anything. The ONLY release path is
//   handleRouteChange acknowledging a trackable route on a genuine React
//   route change — no timers, no polling, no remote callbacks. A query-only
//   URL rewrite on an unchanged pathname therefore leaves collection off
//   until the next acknowledged route change (fail closed by design).
// - The guard collects nothing: no URLs, no navigation metadata, no events.
//   Its only externally visible write is the boolean ga-disable flag.
//
// Deliberate non-goals:
// - Hash-only changes are NOT quarantined: this site's fragments are static
//   semantic anchors (e.g. the booking-section anchors), never user data,
//   and every payload this codebase sends strips the hash anyway.
// - A same-URL replaceState (Next.js scroll/state bookkeeping passes no URL,
//   or the identical URL) is NOT quarantined: it exposes nothing new, and
//   quarantining it would falsely kill attribution on ?utm_* landings.
// - No pageshow/BFCache listener: a document restored from the back/forward
//   cache resumes with its wrapped history methods, module state and
//   ga-disable flag intact, and restoration runs no code that could widen
//   collection — there is no new window to close.

import { isTrackablePath, setAnalyticsCollectionDisabled } from '@/lib/analytics'

type HistoryMethod = History['pushState']
type Restorer = () => void

// Window-level idempotence marker: survives dev hot-reload module
// duplication, so the history methods are never double-wrapped.
const INSTALLED_FLAG = '__reachAnalyticsNavigationGuard'

let restorers: Restorer[] = []

/**
 * Pure transition predicate: is it safe to let this history target become
 * the browser location while collection stays enabled?
 */
export function isSafeHistoryTransitionTarget(
  url: unknown,
  currentHref: string,
): boolean {
  // No URL argument leaves the location untouched (Next.js calls bare
  // replaceState for scroll/state bookkeeping): nothing new is exposed, even
  // when the current location legitimately carries landing UTM params.
  if (url === undefined || url === null || url === '') return true
  let next: URL
  try {
    next = new URL(String(url), currentHref)
  } catch {
    // The native history call will throw on an unparsable target and the
    // location will not change.
    return true
  }
  if (next.href === currentHref) return true // same-URL state replacement
  if (!isTrackablePath(next.pathname)) return false
  // A transition that introduces a query string would hand it to Enhanced
  // Measurement's history-triggered page_view before React can sanitize it.
  return next.search === ''
}

/** The already-committed browser location, judged by the same rules. */
function isSafeObservedLocation(): boolean {
  return (
    isTrackablePath(window.location.pathname) && window.location.search === ''
  )
}

function makeGuardedHistoryMethod(
  gaId: string,
  downstream: HistoryMethod,
): HistoryMethod {
  // Named two-parameter function to keep the native method's call signature;
  // the optional URL argument flows through the real arguments object.
  const guarded = function guardedHistoryMethod(
    this: History,
    data: unknown,
    unused: string,
  ) {
    // eslint-disable-next-line prefer-rest-params
    const args = Array.from(arguments) as Parameters<HistoryMethod>
    if (!isSafeHistoryTransitionTarget(args[2], window.location.href)) {
      // Engage the kill switch BEFORE the downstream method — which may be a
      // later-installed analytics wrapper — can observe the transition.
      setAnalyticsCollectionDisabled(gaId, true)
    }
    const hrefBefore = window.location.href
    const result = downstream.apply(this, args)
    // A nested wrapper below us could have moved the location somewhere the
    // pre-check never saw; re-judge the committed location, but only when it
    // actually changed (an unchanged URL exposes nothing new).
    if (window.location.href !== hrefBefore && !isSafeObservedLocation()) {
      setAnalyticsCollectionDisabled(gaId, true)
    }
    return result
  }
  return guarded
}

function installGuardedHistoryProperty(
  gaId: string,
  name: 'pushState' | 'replaceState',
): Restorer {
  const history = window.history
  const originalOwnDescriptor = Object.getOwnPropertyDescriptor(history, name)
  const enumerable =
    originalOwnDescriptor?.enumerable ??
    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(history), name)
      ?.enumerable ??
    false

  let exposed: HistoryMethod = makeGuardedHistoryMethod(gaId, history[name])
  const getter = () => exposed
  const setter = (value: unknown) => {
    // gtag.js (like any history-instrumenting library) wraps these methods
    // by reading the current value and assigning its own wrapper. Re-wrap
    // every assignment so this guard stays OUTERMOST: the kill switch is
    // engaged before the newly assigned wrapper can inspect the transition.
    exposed =
      typeof value === 'function'
        ? makeGuardedHistoryMethod(gaId, value as HistoryMethod)
        : (value as HistoryMethod)
  }
  Object.defineProperty(history, name, {
    configurable: true,
    enumerable,
    get: getter,
    set: setter,
  })

  return () => {
    const current = Object.getOwnPropertyDescriptor(history, name)
    // If a later library replaced our accessor wholesale, leave its state
    // intact rather than clobbering it on test cleanup.
    if (current?.get !== getter) return
    if (originalOwnDescriptor) {
      Object.defineProperty(history, name, originalOwnDescriptor)
    } else {
      delete (history as unknown as Record<string, unknown>)[name]
    }
  }
}

/**
 * Install the synchronous navigation quarantine. Idempotent — repeated calls
 * (React re-renders, StrictMode double effects, hot reload) are no-ops after
 * the first. Installed from the analytics provider before gtag.js executes;
 * never uninstalled in production (the guard is passive and must outlive any
 * one React owner for the document's lifetime).
 */
export function installAnalyticsNavigationGuard(gaId: string): void {
  if (typeof window === 'undefined' || !gaId) return
  const w = window as unknown as Record<string, unknown>
  if (w[INSTALLED_FLAG]) return
  w[INSTALLED_FLAG] = gaId

  const onPopstate = () => {
    // The browser has already moved the location by the time popstate fires;
    // quarantine synchronously so later-registered listeners (gtag.js loads
    // after this guard is installed) observe the kill switch first.
    if (!isSafeObservedLocation()) setAnalyticsCollectionDisabled(gaId, true)
  }
  window.addEventListener('popstate', onPopstate, true)

  restorers = [
    installGuardedHistoryProperty(gaId, 'pushState'),
    installGuardedHistoryProperty(gaId, 'replaceState'),
    () => window.removeEventListener('popstate', onPopstate, true),
  ]
}

/** Test-only teardown: restores the native history methods and listener. */
export function uninstallAnalyticsNavigationGuardForTests(): void {
  if (typeof window === 'undefined') return
  for (const restore of restorers.splice(0)) restore()
  delete (window as unknown as Record<string, unknown>)[INSTALLED_FLAG]
}
